import React, { Suspense, useEffect } from 'react';
import { Redirect, Route as ReactRouterRoute, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import Loading from 'components/shared-components/Loading';
import { VENDORLINK_PREFIX_PATH } from 'configs/AppConfig';
import { useCurrentAccountUser, useMyAccountUsers } from '../../redux/selector';
import { withSentryRouting } from '@sentry/react';
import LoadCurrentAccount from './LoadCurrentAccount';
import { lazyPreload } from '../app-views/components/LazyPreload';
import { useAccountPrefixPath } from '../../hooks/vendorlink/Routing';
import AppLayout from '../../layouts/app-layout';
import utils from 'utils';
import { NavItem } from '../../layouts/app-layout/NavItem';
import Icon from '../app-views/components/Icon';
import MenuContent from '../../components/layout-components/MenuContent';
import AppBreadcrumb from '../../components/layout-components/AppBreadcrumb';
import AccountSwitcher from './AccountSwitcher';
import NavProfile from './NavProfile';
import { useIdentifyCurrentUserAndMemberIfNeeded } from '../../integration';

const Route = withSentryRouting(ReactRouterRoute);

const LazyAccountOnboarding = lazyPreload(() => import('./account-onboarding'));
export const LazyTransactions = lazyPreload(() => import('./transactions'));
export const LazyAccountReceivable = lazyPreload(() => import('./accounts-receivable'));
export const LazyAccountPayable = lazyPreload(() => import('./accounts-payable'));
export const LazyContacts = lazyPreload(() => import('./account-contact'));
export const LazyAccountPayableBillApprovals = lazyPreload(() => import('./settings'));
export const LazyReports = lazyPreload(() => import('./reports'));

const AccountUserRoutes = () => {
  useEffect(() => {
    LazyTransactions.preload();
    LazyAccountReceivable.preload();
    LazyAccountPayable.preload();
    LazyContacts.preload();
    LazyAccountPayableBillApprovals.preload();
  }, []);
  const { url } = useRouteMatch();
  const prefixPath = useAccountPrefixPath();
  const accountUser = useCurrentAccountUser();
  return (
    <LoadCurrentAccount>
      <Switch>
        <Route path={`${url}/join`} component={LazyAccountOnboarding} />
        {/* Redirects to tryforest.com/app/join or tryforest.com/app/1/join if the member becomes inactive. */}
        {!accountUser && <Redirect to={`${prefixPath}/join`} />}
        <Route path={`${url}/transactions`} component={LazyTransactions} />
        <Route path={`${url}/invoices`} component={LazyAccountReceivable} />
        <Route path={`${url}/payBills/approvalPolicies`} component={LazyAccountPayableBillApprovals} />
        <Route path={`${url}/payBills`} component={LazyAccountPayable} />
        <Route path={`${url}/contacts`} component={LazyContacts} />
        <Route path={`${url}/reports`} component={LazyReports} />
        <Redirect to={`${prefixPath}/invoices`} />
      </Switch>
    </LoadCurrentAccount>
  );
};

export const AppViews = () => {
  // TODO: Identify account user.
  useIdentifyCurrentUserAndMemberIfNeeded();
  const accountUsers = useMyAccountUsers();
  return (
    <Suspense fallback={<Loading cover="content" />}>
      <Switch>
        {/* If the user has any account users, navigate from tryforest.com/vendor/join to tryforest.com/vendor/:accountId */}
        {accountUsers.length === 0 && (
          <Route path={`${VENDORLINK_PREFIX_PATH}/join`} component={LazyAccountOnboarding} />
        )}
        <Route path={`${VENDORLINK_PREFIX_PATH}/:accountId`} component={AccountUserRoutes} />
        <Route path={VENDORLINK_PREFIX_PATH} component={AccountUserRoutes} />
      </Switch>
    </Suspense>
  );
};

const VendorLinkApp = () => {
  const location = useLocation();
  const navItems = useAccountNavigationConfig();
  const currentRouteInfo = utils.getRouteInfo(navItems, location.pathname) as NavItem;
  const currentAccountUser = useCurrentAccountUser();
  return (
    <AppLayout
      navMenuContent={
        <MenuContent
          routeInfo={currentRouteInfo}
          switcher={<AccountSwitcher />}
          navItems={navItems}
          isNavEnabled={!!currentAccountUser}
        />
      }
      navProfile={<NavProfile />}
      pageHeader={<AppBreadcrumb routeInfo={currentRouteInfo} navItems={navItems} />}
      appView={<AppViews />}
    />
  );
};

export const useAccountNavigationConfig = (): NavItem[] => {
  const prefixPath = useAccountPrefixPath();
  return [
    {
      key: 'invoices',
      path: `${prefixPath}/invoices`,
      title: 'Invoices',
      icon: ({ isActive }) => <Icon name="get-paid-fill" />,
      breadcrumb: true,
      submenu: [],
      onMouseEnter: () => {
        LazyAccountReceivable.preload();
      },
    },
    {
      key: 'payBills',
      path: `${prefixPath}/payBills`,
      title: 'Pay bills',
      icon: ({ isActive }) => <Icon name="pay-fill" />,
      breadcrumb: true,
      submenu: [],
      onMouseEnter: () => {
        LazyAccountReceivable.preload();
      },
    },
    {
      key: 'transactions',
      path: `${prefixPath}/transactions`,
      title: 'Banking',
      icon: ({ isActive }) => <Icon name="arrow-left-right" />,
      breadcrumb: true,
      submenu: [],
      onMouseEnter: () => {
        LazyTransactions.preload();
      },
    },
    {
      key: 'contacts',
      path: `${prefixPath}/contacts`,
      title: 'Contacts',
      icon: ({ isActive }) => <Icon name="journal-medical-fill-inverted" />,
      breadcrumb: true,
      submenu: [],
      onMouseEnter: () => {
        LazyContacts.preload();
      },
    },
    {
      key: 'reports',
      path: `${prefixPath}/reports`,
      title: 'Reports',
      icon: ({ isActive }) => <Icon name="journal-medical-fill-inverted" />,
      breadcrumb: true,
      submenu: [],
      onMouseEnter: () => {
        LazyReports.preload();
      },
    },
  ];
};

export default React.memo(VendorLinkApp);
